/**
 * Google Analytics 4 (GA4) Analytics Utility
 * Provides type-safe event tracking for the Mindara application
 */

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag: (
      command: 'config' | 'event' | 'js' | 'set',
      targetId: string | Date | Record<string, any>,
      config?: any
    ) => void;
    dataLayer: any[];
  }
}

// Core Analytics Event Types
export interface BaseAnalyticsEvent {
  event_category?: string;
  event_label?: string;
  value?: number;
  custom_parameters?: Record<string, any>;
}

// Authentication Events
export interface AuthEvent extends BaseAnalyticsEvent {
  method?: 'google_oauth' | 'email_password';
  success?: boolean;
  error_code?: string;
}

// Plan Management Events
export interface PlanEvent extends BaseAnalyticsEvent {
  plan_id?: string;
  plan_slug?: string;
  activity_count?: number;
  plan_type?: 'manual' | 'ai_generated';
  privacy_setting?: 'public' | 'private';
}

// Map Interaction Events
export interface MapEvent extends BaseAnalyticsEvent {
  place_id?: string;
  place_name?: string;
  place_type?: string;
  latitude?: number;
  longitude?: number;
  zoom_level?: number;
  search_query?: string;
  results_count?: number;
  filter_type?: string;
  filter_value?: string;
  previous_mode?: string;
  new_mode?: string;
}

// AI Planner Events
export interface AIEvent extends BaseAnalyticsEvent {
  prompt_text?: string;
  prompt_length?: number;
  user_location?: string;
  locations_count?: number;
  time_taken_ms?: number;
  success?: boolean;
  error_type?: string;
}

// Social Events
export interface SocialEvent extends BaseAnalyticsEvent {
  plan_id?: string;
  action_type?: 'share' | 'rsvp' | 'invite';
  share_method?: 'link_copy' | 'email';
  viewer_state: "guest" | "owner" | "participant" | "invitee";
  is_public: boolean;
  rsvp_status?: 'going' | 'maybe' | 'no';
  invite_count?: number;
}

// Integration Events
export interface IntegrationEvent extends BaseAnalyticsEvent {
  service_name?: 'resy' | 'uber' | 'lyft';
  action_type?: 'connect' | 'disconnect' | 'booking' | 'error';
  restaurant_name?: string;
  party_size?: number;
  booking_time?: string;
  success?: boolean;
}

// Navigation Events
export interface NavigationEvent extends BaseAnalyticsEvent {
  source_page?: string;
  destination_page?: string;
  navigation_method?: 'header' | 'bottom_nav' | 'link' | 'back_button';
}

// Performance Events
export interface PerformanceEvent extends BaseAnalyticsEvent {
  component_name?: string;
  loading_time_ms?: number;
  duration_ms?: number;
  error_type?: string;
  error_message?: string;
  endpoint?: string;
  status_code?: number;
  search_query?: string;
  search_type?: string;
  old_index?: number;
  new_index?: number;
  activity_count?: number;
}

// Union type for all events
export type AnalyticsEventParams = 
  | AuthEvent 
  | PlanEvent 
  | MapEvent 
  | AIEvent 
  | SocialEvent 
  | IntegrationEvent 
  | NavigationEvent 
  | PerformanceEvent;

/**
 * Analytics utility class for GA4 event tracking
 */
export class Analytics {
  private static isEnabled(): boolean {
    return typeof window !== 'undefined' && typeof window.gtag === 'function';
  }


  /**
   * Track a custom event
   */
  static trackEvent(eventName: string, parameters: AnalyticsEventParams = {}): void {
    if (!this.isEnabled()) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Analytics Event:', eventName, parameters);
      }
      return;
    }

    try {
      window.gtag('event', eventName, {
        event_category: parameters.event_category || 'engagement',
        event_label: parameters.event_label,
        value: parameters.value,
        ...parameters.custom_parameters,
        // Remove these from custom_parameters to avoid duplication
        ...Object.fromEntries(
          Object.entries(parameters).filter(([key]) => 
            !['event_category', 'event_label', 'value', 'custom_parameters'].includes(key)
          )
        )
      });
    } catch (error) {
      console.error('Analytics tracking error:', error);
    }
  }

  /**
   * Track page views
   */
  static trackPageView(pagePath: string, pageTitle?: string): void {
    if (!this.isEnabled()) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Page View:', pagePath, pageTitle);
      }
      return;
    }

    try {
      window.gtag('config', 'G-8X96W6DCHB', {
        page_path: pagePath,
        page_title: pageTitle,
      });
    } catch (error) {
      console.error('Page view tracking error:', error);
    }
  }

  /**
   * Set user properties
   */
  static setUserProperties(properties: Record<string, any>): void {
    if (!this.isEnabled()) {
      if (process.env.NODE_ENV === 'development') {
        console.log('User Properties:', properties);
      }
      return;
    }

    try {
      window.gtag('set', properties);
    } catch (error) {
      console.error('User properties error:', error);
    }
  }

  /**
   * Set user ID for cross-device tracking
   */
  static setUserId(userId: string): void {
    if (!this.isEnabled()) {
      if (process.env.NODE_ENV === 'development') {
        console.log('User ID:', userId);
      }
      return;
    }

    try {
      window.gtag('config', 'G-8X96W6DCHB', {
        user_id: userId
      });
    } catch (error) {
      console.error('User ID tracking error:', error);
    }
  }
}

// Convenience functions for common events

/**
 * Authentication Events
 */
export const AuthAnalytics = {
  login: (method: 'google_oauth' | 'email_password', success: boolean = true) => {
    Analytics.trackEvent('login', {
      event_category: 'authentication',
      method,
      success,
      event_label: `${method}_${success ? 'success' : 'failure'}`
    });
  },

  signup: (method: 'google_oauth' | 'email_password', success: boolean = true) => {
    Analytics.trackEvent('sign_up', {
      event_category: 'authentication',
      method,
      success,
      event_label: `${method}_${success ? 'success' : 'failure'}`
    });
  },

  logout: () => {
    Analytics.trackEvent('logout', {
      event_category: 'authentication',
      event_label: 'user_logout'
    });
  },

  trackEvent: (eventName: string, parameters: AnalyticsEventParams = {}) => {
    Analytics.trackEvent(eventName, parameters);
  }
};

/**
 * Plan Management Events
 */
export const PlanAnalytics = {
  create: (planType: 'manual' | 'ai_generated', activityCount: number = 0) => {
    Analytics.trackEvent('plan_create', {
      event_category: 'plan_management',
      plan_type: planType,
      activity_count: activityCount,
      value: activityCount,
      event_label: `${planType}_plan_created`
    });
  },

  save: (planId: string, activityCount: number, planType: 'manual' | 'ai_generated') => {
    Analytics.trackEvent('plan_save', {
      event_category: 'plan_management',
      plan_id: planId,
      activity_count: activityCount,
      plan_type: planType,
      value: activityCount,
      event_label: 'plan_saved'
    });
  },

  delete: (planId: string, activityCount: number) => {
    Analytics.trackEvent('plan_delete', {
      event_category: 'plan_management',
      plan_id: planId,
      activity_count: activityCount,
      event_label: 'plan_deleted'
    });
  },

  edit: (planId: string) => {
    Analytics.trackEvent('plan_edit_start', {
      event_category: 'plan_management',
      plan_id: planId,
      event_label: 'plan_edit_started'
    });
  },

  activityAdd: (planId: string, placeName: string, placeType: string) => {
    Analytics.trackEvent('activity_add_to_plan', {
      event_category: 'plan_management',
      plan_id: planId,
      place_name: placeName,
      place_type: placeType,
      event_label: 'activity_added'
    });
  },

  activityRemove: (planId: string, placeName: string) => {
    Analytics.trackEvent('activity_remove_from_plan', {
      event_category: 'plan_management',
      plan_id: planId,
      place_name: placeName,
      event_label: 'activity_removed'
    });
  },

  trackEvent: (eventName: string, parameters: AnalyticsEventParams = {}) => {
    Analytics.trackEvent(eventName, parameters);
  }
};

/**
 * Map Interaction Events
 */
export const MapAnalytics = {
  markerClick: (placeId: string, placeName: string, placeType: string) => {
    Analytics.trackEvent('map_marker_click', {
      event_category: 'map_interaction',
      place_id: placeId,
      place_name: placeName,
      place_type: placeType,
      event_label: `marker_${placeType}`
    });
  },

  search: (query: string, resultsCount: number) => {
    Analytics.trackEvent('places_search', {
      event_category: 'map_interaction',
      search_query: query,
      results_count: resultsCount,
      value: resultsCount,
      event_label: 'places_search_performed'
    });
  },

  filterChange: (filterType: string, filterValue: string) => {
    Analytics.trackEvent('map_filter_change', {
      event_category: 'map_interaction',
      filter_type: filterType,
      filter_value: filterValue,
      event_label: `filter_${filterType}_${filterValue}`
    });
  },

  drawerOpen: (source: 'marker' | 'search', placeName: string) => {
    Analytics.trackEvent('activity_drawer_open', {
      event_category: 'map_interaction',
      place_name: placeName,
      custom_parameters: { source },
      event_label: `drawer_opened_${source}`
    });
  },

  trackEvent: (eventName: string, parameters: AnalyticsEventParams = {}) => {
    Analytics.trackEvent(eventName, parameters);
  }
};

/**
 * AI Planner Events
 */
export const AIAnalytics = {
  promptSubmit: (promptText: string, userLocation: string) => {
    Analytics.trackEvent('ai_prompt_submit', {
      event_category: 'ai_planner',
      prompt_text: promptText,
      prompt_length: promptText.length,
      user_location: userLocation,
      value: promptText.length,
      event_label: 'ai_prompt_submitted'
    });
  },

  planningComplete: (locationsGenerated: number, timeTakenMs: number, success: boolean) => {
    Analytics.trackEvent('ai_planning_complete', {
      event_category: 'ai_planner',
      locations_count: locationsGenerated,
      time_taken_ms: timeTakenMs,
      success,
      event_label: `ai_planning_${success ? 'success' : 'failure'}`
    });
  },

  locationSelect: (locationName: string, sequence: number) => {
    Analytics.trackEvent('ai_location_select', {
      event_category: 'ai_planner',
      place_name: locationName,
      custom_parameters: { sequence },
      event_label: 'ai_location_selected'
    });
  },

  trackEvent: (eventName: string, parameters: AnalyticsEventParams = {}) => {
    Analytics.trackEvent(eventName, parameters);
  }
};

/**
 * Social Events
 */
export const SocialAnalytics = {
  planShare: (planId: string, method: 'link_copy' | 'email') => {
    Analytics.trackEvent('plan_share', {
      event_category: 'social',
      plan_id: planId,
      share_method: method,
      event_label: `plan_shared_${method}`
    });
  },

  rsvp: (planId: string, status: 'going' | 'maybe' | 'no') => {
    Analytics.trackEvent('plan_rsvp', {
      event_category: 'social',
      plan_id: planId,
      rsvp_status: status,
      event_label: `rsvp_${status}`
    });
  },

  invite: (planId: string, inviteCount: number) => {
    Analytics.trackEvent('plan_invite_send', {
      event_category: 'social',
      plan_id: planId,
      invite_count: inviteCount,
      value: inviteCount,
      event_label: 'invites_sent'
    });
  },

  // view: (planId: string, viewerState: string) => {
  //   Analytics.trackEvent('shared_plan', {
  //     event_category: 'social',
  //     plan_id: planId,
  //     viewer_state: viewerState,
  //     event_label: 'shared_plan_view'
  //   });
  // },

  trackEvent: (eventName: string, parameters: AnalyticsEventParams = {}) => {
    Analytics.trackEvent(eventName, parameters);
  }
};

/**
 * Navigation Events
 */
export const NavigationAnalytics = {
  bottomNav: (destination: string) => {
    Analytics.trackEvent('bottom_nav_click', {
      event_category: 'navigation',
      destination_page: destination,
      navigation_method: 'bottom_nav',
      event_label: `bottom_nav_${destination}`
    });
  },

  headerMenu: (destination: string) => {
    Analytics.trackEvent('header_menu_click', {
      event_category: 'navigation',
      destination_page: destination,
      navigation_method: 'header',
      event_label: `header_${destination}`
    });
  }
};

/**
 * Integration Events
 */
export const IntegrationAnalytics = {
  resyBooking: (restaurantName: string, partySize: number, success: boolean) => {
    Analytics.trackEvent('resy_booking_attempt', {
      event_category: 'integrations',
      service_name: 'resy',
      restaurant_name: restaurantName,
      party_size: partySize,
      success,
      value: partySize,
      event_label: `resy_booking_${success ? 'success' : 'failure'}`
    });
  },

  serviceConnect: (serviceName: 'resy' | 'uber' | 'lyft', success: boolean) => {
    Analytics.trackEvent('integration_connect', {
      event_category: 'integrations',
      service_name: serviceName,
      success,
      event_label: `${serviceName}_connect_${success ? 'success' : 'failure'}`
    });
  },

  trackEvent: (eventName: string, parameters: AnalyticsEventParams = {}) => {
    Analytics.trackEvent(eventName, parameters);
  }
};

/**
 * Error Tracking & Performance Events
 */
export const ErrorAnalytics = {
  appError: (errorType: string, componentName: string, errorMessage?: string) => {
    Analytics.trackEvent('app_error', {
      event_category: 'error',
      error_type: errorType,
      component_name: componentName,
      error_message: errorMessage,
      event_label: `error_${errorType}_${componentName}`
    });
  },

  apiError: (endpoint: string, statusCode: number, errorMessage?: string) => {
    Analytics.trackEvent('api_error', {
      event_category: 'error',
      endpoint: endpoint,
      status_code: statusCode,
      error_message: errorMessage,
      value: statusCode,
      event_label: `api_error_${statusCode}`
    });
  },

  loadingTime: (componentName: string, durationMs: number) => {
    Analytics.trackEvent('loading_time', {
      event_category: 'performance',
      component_name: componentName,
      duration_ms: durationMs,
      value: durationMs,
      event_label: `loading_${componentName}`
    });
  },

  searchNoResults: (query: string, searchType: string) => {
    Analytics.trackEvent('search_no_results', {
      event_category: 'performance',
      search_query: query,
      search_type: searchType,
      event_label: `no_results_${searchType}`
    });
  }
};

export default Analytics;